import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_super_admin: boolean;
  totp_enabled: boolean;
  organisation_id: number;
  custom_permissions: any;
  workspace_layout: any;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string, totp?: string) => Promise<any>;
  logout: () => void;
  loading: boolean;
  sessionExpiringSoon: boolean;  // true when <5 min left and no refresh token available
  extendSession: () => void;     // manually trigger a token refresh
}

const AuthContext = createContext<AuthContextType | null>(null);

// ── JWT helpers ────────────────────────────────────────────────────────────────

function getTokenExpiry(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return (payload.exp || 0) * 1000;   // convert to ms
  } catch {
    return 0;
  }
}

// ── Provider ──────────────────────────────────────────────────────────────────

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser]     = useState<User | null>(null);
  const [token, setToken]   = useState<string | null>(localStorage.getItem('lemai_token'));
  const [loading, setLoading] = useState(true);
  const [sessionExpiringSoon, setSessionExpiringSoon] = useState(false);
  const refreshTimerRef     = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimerRef        = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefreshingRef     = useRef(false);

  // ── Proactive token refresh ────────────────────────────────────────────────
  function scheduleRefresh(accessToken: string) {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    const expiry = getTokenExpiry(accessToken);
    if (!expiry) return;
    // Refresh 5 minutes before expiry, minimum 30 seconds from now
    const delay = Math.max(30_000, expiry - Date.now() - 5 * 60 * 1000);
    refreshTimerRef.current = setTimeout(doRefresh, delay);
    // Warn 6 minutes before expiry (1 min before auto-refresh fires)
    const warnDelay = expiry - Date.now() - 6 * 60 * 1000;
    if (warnDelay > 0) {
      warnTimerRef.current = setTimeout(() => setSessionExpiringSoon(true), warnDelay);
    }
  }

  async function doRefresh(): Promise<string | null> {
    if (isRefreshingRef.current) return null;
    const refreshToken = localStorage.getItem('lemai_refresh_token');
    if (!refreshToken) { doLogout(); return null; }
    isRefreshingRef.current = true;
    try {
      const res = await axios.post(
        `${API}/auth/refresh`,
        {},
        { headers: { Authorization: `Bearer ${refreshToken}` } }
      );
      const newToken = res.data.access_token;
      localStorage.setItem('lemai_token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setSessionExpiringSoon(false);
      scheduleRefresh(newToken);
      return newToken;
    } catch {
      // Refresh token expired or revoked — log the user out cleanly
      doLogout();
      return null;
    } finally {
      isRefreshingRef.current = false;
    }
  }

  function doLogout() {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    setSessionExpiringSoon(false);
    // Best-effort server-side logout
    const tok = localStorage.getItem('lemai_token');
    if (tok) {
      axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${tok}` } }).catch(() => {});
    }
    localStorage.removeItem('lemai_token');
    localStorage.removeItem('lemai_refresh_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }

  // ── Axios response interceptor — handle 401 with silent refresh ────────────
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      res => res,
      async err => {
        const originalRequest = err.config;
        // Only attempt refresh on 401, not on the refresh endpoint itself, and only once
        if (
          err.response?.status === 401 &&
          !originalRequest._retried &&
          !originalRequest.url?.includes('/auth/refresh') &&
          !originalRequest.url?.includes('/auth/login')
        ) {
          originalRequest._retried = true;
          const newToken = await doRefresh();
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── On mount — validate stored token ──────────────────────────────────────
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Check if token is expired before making the /me call
      const expiry = getTokenExpiry(token);
      if (expiry && expiry < Date.now()) {
        // Token already expired — try refresh immediately
        doRefresh()
          .then(newToken => {
            if (newToken) {
              axios.get(`${API}/auth/me`)
                .then(res => setUser(res.data.user))
                .catch(() => doLogout())
                .finally(() => setLoading(false));
            } else {
              setLoading(false);
            }
          });
        return;
      }
      axios.get(`${API}/auth/me`)
        .then(res => {
          setUser(res.data.user);
          scheduleRefresh(token);
        })
        .catch(() => { setToken(null); localStorage.removeItem('lemai_token'); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = async (email: string, password: string, totp?: string) => {
    const res = await axios.post(`${API}/auth/login`, { email, password, totp_token: totp });
    if (res.data.requires_totp) return { requires_totp: true };

    const { access_token, refresh_token, user: userData } = res.data;

    localStorage.setItem('lemai_token', access_token);
    if (refresh_token) localStorage.setItem('lemai_refresh_token', refresh_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    setToken(access_token);
    setUser(userData);
    scheduleRefresh(access_token);

    return { success: true, user: userData };
  };

  const extendSession = useCallback(() => {
    setSessionExpiringSoon(false);
    doRefresh(); // eslint-disable-line react-hooks/exhaustive-deps
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{ user, token, login, logout: doLogout, loading, sessionExpiringSoon, extendSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
